'use client';

import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { motion } from 'framer-motion'
import { LogOut, Menu, X, User, ArrowRight, ChevronDown } from 'lucide-react'
import { ShinyButton } from '@/components/ui/shiny-button'
import { NavbarGenerateButton } from '@/components/ui/navbar-generate-button'

export function MarketingNavbar({ isSignedIn }: { isSignedIn?: boolean }) {
    const session = isSignedIn;
    const router = useRouter()
    const [mobileOpen, setMobileOpen] = useState(false)

    return (
        <motion.nav
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="fixed top-6 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-[940px] 2xl:max-w-5xl"
        >
            <div className="sharp-edge backdrop-blur-xl rounded-full px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between shadow-[0_10px_40px_-10px_rgba(0,102,255,0.2)]">
                <Link href="/" className="flex items-center gap-2 group">
                    <Image src="/logo.png" alt="Lenklyst Logo" width={32} height={32} className="object-contain transition-transform duration-300" style={{ width: '32px', height: '32px' }} priority />
                    <span className="text-white/20 font-light mx-0.5 select-none">|</span>
                    <span className="text-lg font-bold tracking-tighter text-white group-hover:text-primary transition-colors">
                        Lenklyst
                    </span>
                </Link>

                {/* Desktop nav links */}
                <div className="hidden sm:flex items-center gap-6">
                    <Link href="/" className="text-white/50 hover:text-white text-sm font-medium transition-colors">Home</Link>
                    <Link href="/features" className="text-white/50 hover:text-white text-sm font-medium transition-colors">Features</Link>
                    <Link href="/pricing" className="text-white/50 hover:text-white text-sm font-medium transition-colors">Pricing</Link>
                    <Link href="/about" className="text-white/50 hover:text-white text-sm font-medium transition-colors">About</Link>
                    
                    {/* Resources Dropdown */}
                    <div className="relative group">
                        <button className="flex items-center gap-1 text-white/50 hover:text-white text-sm font-medium transition-colors py-1">
                            Resources
                            <ChevronDown className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                        </button>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 hidden group-hover:block w-40 z-50">
                            <div className="bg-black/95 backdrop-blur-xl border border-white/10 rounded-2xl p-2 shadow-2xl space-y-0.5">
                                {[
                                    { label: 'Blog', href: '/blog' },
                                    { label: 'Comparisons', href: '/vs' },
                                    { label: 'Use Cases', href: '/use-cases' },
                                    { label: 'Guides', href: '/guides' },
                                    { label: 'How It Works', href: '/how-it-works' },
                                ].map((item) => (
                                    <Link
                                        key={item.label}
                                        href={item.href}
                                        className="block px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white/50 hover:text-white hover:bg-white/5 rounded-xl transition-all"
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Auth actions */}
                <div className="flex items-center gap-2 sm:gap-3">
                    <NavbarGenerateButton isSignedIn={session} />

                    {session ? (
                        <div className="hidden sm:flex items-center gap-3">
                            <Link href="/dashboard" className="text-white font-semibold text-sm px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                                Dashboard
                            </Link>
                            <Link
                                href="/auth/sign-out"
                                className="px-3 py-1.5 text-white/50 rounded-full text-sm font-medium hover:text-white/80 transition-all flex items-center gap-1.5 active:scale-95 duration-200"
                            >
                                <LogOut className="w-3.5 h-3.5" />
                                <span>Logout</span>
                            </Link>
                        </div>
                    ) : (
                        <div className="hidden sm:flex items-center gap-3">
                            <Link href="/auth/login" className="text-white/60 hover:text-white transition-colors text-sm font-medium">Login</Link>
                            <ShinyButton
                                onClick={() => router.push('/auth/sign-up')}
                                className="whitespace-nowrap"
                            >
                                Get Started <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                            </ShinyButton>
                        </div>
                    )}

                    {/* Mobile hamburger */}
                    <button
                        onClick={() => setMobileOpen((o) => !o)}
                        className="sm:hidden p-2 rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-all active:scale-95 ml-1"
                        aria-label="Toggle menu"
                    >
                        {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {/* Mobile slide-down menu */}
            <motion.div
                initial={false}
                animate={mobileOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden mt-2"
            >
                <div className="sharp-edge backdrop-blur-xl rounded-2xl px-5 py-4 flex flex-col gap-1 shadow-[0_10px_40px_-10px_rgba(0,102,255,0.2)]">
                    {/* Nav links */}
                    {[
                        { label: 'Home', href: '/' },
                        { label: 'Features', href: '/features' },
                        { label: 'Pricing', href: '/pricing' },
                        { label: 'About', href: '/about' },
                        { label: 'How It Works', href: '/how-it-works' },
                        { label: 'Blog', href: '/blog' },
                        { label: 'Comparisons', href: '/vs' },
                        { label: 'Use Cases', href: '/use-cases' },
                        { label: 'Guides', href: '/guides' },
                    ].map((l) => (
                        <a
                            key={l.label}
                            href={l.href}
                            onClick={() => setMobileOpen(false)}
                            className="block px-4 py-2 text-base font-medium text-white/70 hover:text-white hover:bg-white/5 rounded-xl transition-all"
                        >
                            {l.label}
                        </a>
                    ))}

                    {/* Auth */}
                    <div className="pt-3 flex flex-col gap-2">
                        {session ? (
                            <>
                                <Link
                                    href="/dashboard"
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-white/10 text-white font-semibold text-sm hover:bg-white/20 transition-colors"
                                >
                                    <User className="w-4 h-4" />
                                    Dashboard
                                </Link>
                                <Link
                                    href="/auth/sign-out"
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-full text-white/50 text-sm hover:text-white/80 transition-colors"
                                >
                                    <LogOut className="w-4 h-4" />
                                    Logout
                                </Link>
                            </>
                        ) : (
                            <>
                                <Link
                                    href="/auth/login"
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center justify-center px-4 py-3 rounded-full text-white/60 text-sm font-medium hover:text-white border border-white/10 hover:border-white/20 transition-all"
                                >
                                    Login
                                </Link>
                                <ShinyButton
                                    onClick={() => {
                                        setMobileOpen(false)
                                        router.push('/auth/sign-up')
                                    }}
                                    className="w-full"
                                >
                                    Get Started Free <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                </ShinyButton>
                            </>
                        )}
                    </div>
                </div>
            </motion.div>
        </motion.nav>
    )
}
